import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { Gift, Copy } from "lucide-react";

// Admin: give someone the full Club plan free for a set period (pilots, testers, partners)
export function CompClubs() {
  const qc = useQueryClient();
  const { data = [] } = useQuery({ queryKey: ["comp-clubs"], queryFn: async () => (await api.get("/api/admin/comp-clubs")).data });
  const [f, setF] = useState({ email: "", club_name: "", days: 30, seats: 30 });
  const [busy, setBusy] = useState(false);
  const field = "h-10 rounded-lg bg-[#0B0D11] border border-[#1E222A] px-3 text-sm w-full";
  const give = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.post("/api/admin/comp-club", { ...f, days: Number(f.days), seats: Number(f.seats) });
      toast.success(`${r.owner} now has the Club plan until ${new Date(r.until).toLocaleDateString()}. Club code ${r.club.invite_code}`, { duration: 9000 });
      setF({ ...f, email: "", club_name: "" });
      qc.invalidateQueries({ queryKey: ["comp-clubs"] });
    } catch (err) { toast.error(apiErr(err)); }
    setBusy(false);
  };
  const end = async (c) => {
    if (!window.confirm(`End the free Club period for ${c.name} now?`)) return;
    try { await api.post(`/api/admin/comp-club/${c.id}/end`); qc.invalidateQueries({ queryKey: ["comp-clubs"] }); } catch (err) { toast.error(apiErr(err)); }
  };
  return (
    <div className="relative overflow-hidden rounded-2xl border border-acid/30 bg-gradient-to-br from-[#12160A] to-[#0B0D11] p-5" data-testid="comp-clubs">
      <p className="font-heading font-bold flex items-center gap-2"><Gift className="w-4 h-4 text-acid" /> Complimentary club access</p>
      <p className="text-sm text-[#8A91A6] mt-1">Give a coach or tester the full Club plan for free. They must have signed up in the app first. Their athletes join with the club code. It switches off on its own when the period ends.</p>
      <div className="grid grid-cols-1 md:grid-cols-6 gap-2 mt-3">
        <input className={`${field} md:col-span-2`} placeholder="Owner's email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="comp-email" />
        <input className={`${field} md:col-span-2`} placeholder="Club name (optional)" value={f.club_name} onChange={(e) => setF({ ...f, club_name: e.target.value })} />
        <label className="text-xs text-[#8A91A6]">days<input className={field} type="number" min="1" max="365" value={f.days} onChange={(e) => setF({ ...f, days: e.target.value })} /></label>
        <label className="text-xs text-[#8A91A6]">places<input className={field} type="number" min="1" max="200" value={f.seats} onChange={(e) => setF({ ...f, seats: e.target.value })} /></label>
      </div>
      <button disabled={busy || !f.email} onClick={give} data-testid="comp-give" className="mt-3 h-10 px-5 rounded-full bg-acid text-black font-medium disabled:opacity-50">{busy ? "Setting up…" : "Give free Club access"}</button>
      {data.length > 0 && (
        <div className="mt-4 space-y-2">
          {data.map((c) => {
            const live = c.status === "active" && new Date(c.comp_until) > new Date();
            return (
              <div key={c.id} className="flex flex-wrap items-center gap-3 rounded-lg border border-[#1E222A] bg-[#0B0D11] px-3 py-2 text-sm">
                <span className="font-semibold">{c.name}</span>
                <span className="text-[#8A91A6]">{c.owner_email}</span>
                <button onClick={() => { navigator.clipboard?.writeText(c.invite_code); toast.success("Code copied"); }} className="font-mono2 text-acid inline-flex items-center gap-1">{c.invite_code} <Copy className="w-3 h-3" /></button>
                <span className="text-[#8A91A6]">{c.members}/{c.seats} athletes</span>
                <span className={`mono-label ${live ? "text-acid" : "text-[#8A91A6]"}`}>{live ? `free until ${new Date(c.comp_until).toLocaleDateString()}` : "ended"}</span>
                {live && <button onClick={() => end(c)} className="ml-auto text-xs text-[#8A91A6] hover:text-pulseError">End now</button>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
